import { Fragment } from "react";
import { Navigate, Outlet, Route } from "react-router";
import { PackagingList } from "./PackagingsList";
import { CreatePackagingElement } from "./PackagingForm/Element/CreatePackagingElement";
import { CreatePackagingComposite } from "./PackagingForm/Composite/CreatePackagingComposite";
import { UpdatePackaging } from "./PackagingForm/UpdatePackaging";
import { PackagingContextProvider } from "./PackagingDetails/PackagingContext";
import { PackagingPrimaryTabs } from "./PackagingDetails/PackagingPrimaryTabs";
import { Forbiddenpage } from "src/components/PageState/ForbiddenPage";
import { CREATE, PACKAGING, MODIFY, useFoodpilotContext, VIEW, VIEW_SSQ } from "src/context/FoodpilotContext";
import { Slugs } from "src/context/ProtectedContext";
import { PackagingScores } from "./PackagingDetails/PackagingScores";
import { Informations } from "./PackagingDetails/Informations";
import PackagingScoreContextProvider from "src/pages/Packaging/PackagingDetails/Scores/PackagingScoreContextProvider";
import { ScoreDetail } from "src/components/ScoreDetail/ScoreDetail";
import { DimensionTreeSwitch } from "src/components/ScoreDetail/Dimensions/DimensionTreeSwitch";
import { EntityScoresProvider } from "src/context/Scores/provider";
import { RouteErrorPage } from "src/components/PageState/RouteErrorPage";

export const usePackagingRouter = () => {
    return (
        <Fragment>
            <Route path="packagings" element={<PackagingScoresContext />} errorElement={<RouteErrorPage />}>
                <Route element={<PackagingActionProtection slug={VIEW} />}>
                    <Route index element={<PackagingList />} />
                </Route>

                <Route path={"new"} element={<PackagingActionProtection slug={CREATE} />}>
                    {/*
                     * STI-aware creation flow:
                     *  - `/new` → packaging list (the creation modal lives there).
                     *  - `/new/element` → elementary packaging.
                     *  - `/new/composite` → composite packaging, either generic
                     *    (sure-side reference) or specific (user-built).
                     */}
                    <Route index element={<Navigate to=".." replace />} />
                    <Route path="element" element={<CreatePackagingElement />} />
                    <Route path="composite" element={<CreatePackagingComposite />} />
                </Route>

                <Route path=":packagingId" element={<ExistingPackaging />}>
                    <Route path={"edit"} element={<PackagingActionProtection slug={MODIFY} />}>
                        <Route element={<UpdatePackaging />}>
                            <Route index />
                            <Route path={":startingPageId"} />
                        </Route>
                    </Route>

                    <Route element={<PackagingActionProtection slug={VIEW} />}>
                        <Route index element={<Navigate to="scores" replace />} />

                        <Route path="scores" element={<PackagingPrimaryTabs selectedSection="scores" />}>
                            <Route index element={<PackagingScores />} />

                            <Route path=":scoreId" element={<ExistingPackagingScore />}>
                                <Route index element={<ScoreDetail selectedTab={null} />} />
                                <Route path="distribution" element={<ScoreDetail selectedTab={"distribution"} />}>
                                    <Route index element={<DimensionTreeSwitch mode={"distribution"} />} />
                                </Route>
                            </Route>
                        </Route>

                        <Route element={<PackagingActionProtection slug={VIEW_SSQ} />}>
                            <Route path={"questionnaire"} element={<Navigate to="../scores" replace />} />
                            <Route
                                path="questionnaire/edit/:headingId"
                                element={<Navigate to="../../scores" replace />}
                            />
                        </Route>

                        <Route path={"informations"}>
                            <Route element={<PackagingPrimaryTabs selectedSection={"informations"} />}>
                                <Route index element={<Informations />} />
                            </Route>
                        </Route>
                    </Route>
                </Route>
            </Route>
        </Fragment>
    );
};

type PackagingActionProtectionProps = {
    slug: Slugs;
};
const PackagingActionProtection = (props: PackagingActionProtectionProps) => {
    const { slug } = props;
    const { canUser } = useFoodpilotContext();

    if (canUser(slug, PACKAGING) === false) {
        return <Forbiddenpage />;
    }

    return <Outlet />;
};

const ExistingPackaging = () => {
    return (
        <PackagingContextProvider>
            <Outlet />
        </PackagingContextProvider>
    );
};

const ExistingPackagingScore = () => {
    return (
        <PackagingScoreContextProvider>
            <Outlet />
        </PackagingScoreContextProvider>
    );
};

const PackagingScoresContext = () => {
    return (
        <EntityScoresProvider entity="packaging">
            <Outlet />
        </EntityScoresProvider>
    );
};
